/* Pedagogical Support Plans (Plans de Soutien Pédagogique) — iter82.
 * Auto-generated from Predictive Analytics for at-risk students.
 * Route: /support-plans
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Lifebuoy, Sparkle, ArrowClockwise, FilePdf, Trash, PencilSimple,
  CheckSquare, Square, Plus, ChatCircle, Warning, EnvelopeSimple,
  BellRinging, GridFour,
} from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SupportPlansCohortMatrix } from '../components/support/SupportPlansCohortMatrix';
import api from '../lib/api';

const RISK_META = {
  critical: { label: 'Critique', color: '#B91C1C', bg: '#FEE2E2', icon: '🔴' },
  at_risk: { label: 'À risque', color: '#DC2626', bg: '#FEF2F2', icon: '🟠' },
  warning: { label: 'Vigilance', color: '#B45309', bg: '#FEF3C7', icon: '🟡' },
  safe: { label: 'Sécurisé', color: '#059669', bg: '#ECFDF5', icon: '🟢' },
};
const STATUS_META = {
  draft: { label: 'Brouillon', color: '#64748B', bg: '#F1F5F9' },
  active: { label: 'Actif', color: '#0052FF', bg: '#DBEAFE' },
  completed: { label: 'Terminé', color: '#059669', bg: '#ECFDF5' },
  archived: { label: 'Archivé', color: '#94A3B8', bg: '#F8FAFC' },
};
const PRIORITY_META = {
  high: { label: 'Haute', color: '#B91C1C' },
  medium: { label: 'Moyenne', color: '#B45309' },
  low: { label: 'Basse', color: '#059669' },
};

export default function SupportPlans() {
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState('');
  const [schoolYear, setSchoolYear] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);   // full detail
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewMean, setReviewMean] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('draft');
  const [editRecos, setEditRecos] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, y] = await Promise.all([
          api.get('/classes').then((r) => r.data),
          api.get('/school-years/active').then((r) => r.data),
        ]);
        setClasses(c);
        setSchoolYear(y?.name || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`);
        if (c.length) setClassName(c[0].name);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!className || !schoolYear) return;
    setLoading(true);
    try {
      const params = { class_name: className, school_year: schoolYear };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/support-plans', { params });
      setItems(data.items || []);
      setKpi(data.kpi || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [className, schoolYear, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!className || !schoolYear) return;
    setGenerating(true);
    try {
      const { data } = await api.post('/support-plans/auto-generate', {
        class_name: className, school_year: schoolYear,
      });
      toast.success(
        `${data.created} plan(s) créé(s), ${data.updated} mis à jour (${data.total} au total)`,
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur génération');
    } finally { setGenerating(false); }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/support-plans/${id}`);
      setSelected(data);
    } catch (e) { toast.error('Erreur détail'); }
  };

  const toggleAction = async (idx) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(
        `/support-plans/${selected.id}/actions/${idx}`,
      );
      setSelected(data);
      // refresh list totals
      setItems((prev) => prev.map((p) => (p.id === data.id ? data : p)));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openReview = () => {
    setReviewNote(''); setReviewMean(''); setReviewOpen(true);
  };
  const submitReview = async () => {
    if (!reviewNote.trim()) { toast.error('Note requise'); return; }
    try {
      const body = { note: reviewNote };
      if (reviewMean !== '') body.new_mean = parseFloat(reviewMean);
      const { data } = await api.post(
        `/support-plans/${selected.id}/reviews`, body,
      );
      setSelected(data);
      setItems((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      setReviewOpen(false);
      toast.success('Point d\'étape ajouté');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEdit = () => {
    if (!selected) return;
    setEditNotes(selected.notes || '');
    setEditStatus(selected.status || 'draft');
    setEditRecos((selected.recommendations || []).map((r) => ({ ...r })));
    setEditOpen(true);
  };
  const submitEdit = async () => {
    try {
      const { data } = await api.put(`/support-plans/${selected.id}`, {
        notes: editNotes,
        status: editStatus,
        recommendations: editRecos,
      });
      setSelected(data);
      setItems((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      setEditOpen(false);
      toast.success('Plan mis à jour');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const removePlan = async (id) => {
    if (!window.confirm('Supprimer définitivement ce plan ?')) return;
    try {
      await api.delete(`/support-plans/${id}`);
      toast.success('Plan supprimé');
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) { toast.error('Erreur suppression'); }
  };

  const resendNotification = async (id) => {
    try {
      const { data } = await api.post(`/support-plans/${id}/notify-parents`);
      const rep = data.report || {};
      if (rep.no_parents) {
        toast.warning('Aucun parent lié à cet élève');
      } else {
        toast.success(
          `Notification envoyée — ${rep.emailed} email(s), ${rep.sms} SMS, ${rep.push} push`,
        );
      }
      await load();
      if (selected?.id === id) await openDetail(id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
  };

  const downloadPdf = (id) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/support-plans/pdf/${id}`;
    window.open(url, '_blank');
  };

  const filteredItems = useMemo(() => items, [items]);

  return (
    <div className="p-6 space-y-6" data-testid="support-plans-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-[#0F172A] flex items-center gap-2">
            <Lifebuoy size={28} weight="duotone" className="text-[#0052FF]" />
            Plans de soutien pédagogique
          </h1>
          <p className="text-sm text-[#64748B] max-w-2xl">
            Génération automatique de plans personnalisés pour les élèves à risque
            (critique / à risque). Recommandations, actions, points d'étape et export PDF
            pour signature parent.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Classe</Label>
            <Select value={className} onValueChange={setClassName}>
              <SelectTrigger className="w-40 h-9" data-testid="sp-class-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Année scolaire</Label>
            <Input
              value={schoolYear} onChange={(e) => setSchoolYear(e.target.value)}
              className="w-32 h-9" data-testid="sp-year-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#64748B]">Statut</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 h-9" data-testid="sp-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="completed">Terminé</SelectItem>
                <SelectItem value="archived">Archivé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} variant="outline" data-testid="sp-refresh">
            <ArrowClockwise size={14} className="mr-1" /> Actualiser
          </Button>
          <Button
            onClick={generate} disabled={generating}
            data-testid="sp-generate-btn"
            className="bg-[#0052FF] hover:bg-[#0044CC]">
            <Sparkle size={14} className="mr-1" />
            {generating ? 'Génération…' : 'Générer plans (élèves à risque)'}
          </Button>
        </div>
      </div>

      {/* KPI row */}
      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card><CardContent className="p-3">
            <p className="text-[10px] uppercase text-[#64748B] font-bold">Total</p>
            <p className="text-2xl font-outfit font-bold"
                data-testid="sp-kpi-total">{kpi.total}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#FEE2E2]">
            <p className="text-[10px] uppercase font-bold text-red-800">🔴 Critique</p>
            <p className="text-2xl font-outfit font-bold text-red-700"
                data-testid="sp-kpi-critical">{kpi.critical}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#FEF2F2]">
            <p className="text-[10px] uppercase font-bold text-red-700">🟠 À risque</p>
            <p className="text-2xl font-outfit font-bold text-red-600"
                data-testid="sp-kpi-atrisk">{kpi.at_risk}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#F1F5F9]">
            <p className="text-[10px] uppercase font-bold text-slate-700">Brouillons</p>
            <p className="text-2xl font-outfit font-bold text-slate-800">{kpi.draft}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#DBEAFE]">
            <p className="text-[10px] uppercase font-bold text-blue-800">Actifs</p>
            <p className="text-2xl font-outfit font-bold text-blue-700"
                data-testid="sp-kpi-active">{kpi.active}</p>
          </CardContent></Card>
          <Card><CardContent className="p-3 bg-[#ECFDF5]">
            <p className="text-[10px] uppercase font-bold text-emerald-800">Terminés</p>
            <p className="text-2xl font-outfit font-bold text-emerald-700">{kpi.completed}</p>
          </CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="list" data-testid="sp-tabs">
        <TabsList>
          <TabsTrigger value="list" data-testid="sp-tab-list">Plans</TabsTrigger>
          <TabsTrigger value="cohort" data-testid="sp-tab-cohort">
            <GridFour size={14} className="mr-1" /> Matrice cohorte
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {/* Table */}
          <Card>
            <CardContent className="p-4">
          {loading ? (
            <p className="text-center py-10 text-[#94A3B8]">Chargement…</p>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-10">
              <Warning size={40} className="mx-auto text-[#CBD5E1] mb-2" weight="duotone" />
              <p className="text-sm text-[#64748B]">
                Aucun plan pour ce filtre. Cliquez « Générer plans » pour créer
                automatiquement les plans des élèves à risque.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="sp-plans-table">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Élève</th>
                  <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Risque</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Moyenne</th>
                  <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Matières</th>
                  <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Actions</th>
                  <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Statut</th>
                  <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Ops</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((p) => {
                  const rm = RISK_META[p.risk_level] || RISK_META.warning;
                  const sm = STATUS_META[p.status] || STATUS_META.draft;
                  const doneCount = (p.actions || []).filter((a) => a.done).length;
                  const totalActions = (p.actions || []).length;
                  return (
                    <tr key={p.id}
                        className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]"
                        data-testid={`sp-row-${p.id}`}>
                      <td className="py-2 px-2 font-medium">{p.student_name}</td>
                      <td className="py-2 px-2 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: rm.bg, color: rm.color }}>
                          {rm.icon} {rm.label}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right font-semibold">
                        {p.overall_mean != null ? `${p.overall_mean}/20` : '—'}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className="text-red-700 font-bold">{p.critical_subjects || 0}</span>
                        {' / '}
                        <span className="text-amber-700 font-bold">{p.at_risk_subjects || 0}</span>
                      </td>
                      <td className="py-2 px-2 text-center text-xs">
                        {doneCount}/{totalActions}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: sm.bg, color: sm.color }}>
                          {sm.label}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openDetail(p.id)}
                            data-testid={`sp-open-${p.id}`}>Ouvrir</Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadPdf(p.id)}
                            data-testid={`sp-pdf-${p.id}`} title="Télécharger PDF">
                            <FilePdf size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => removePlan(p.id)}
                            data-testid={`sp-del-${p.id}`} title="Supprimer">
                            <Trash size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="cohort">
          <SupportPlansCohortMatrix
            className={className}
            schoolYear={schoolYear}
          />
        </TabsContent>
      </Tabs>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="sp-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Lifebuoy size={20} weight="duotone" className="text-[#0052FF]" />
                  Plan de soutien — {selected.student_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div><span className="text-[#64748B]">Classe :</span> <b>{selected.class_name}</b></div>
                  <div><span className="text-[#64748B]">Année :</span> <b>{selected.school_year}</b></div>
                  <div><span className="text-[#64748B]">Moyenne :</span>
                    <b> {selected.overall_mean ?? '—'}/20</b></div>
                  <div><span className="text-[#64748B]">Statut :</span>
                    <Badge className="ml-1" style={{
                      background: (STATUS_META[selected.status] || {}).bg,
                      color: (STATUS_META[selected.status] || {}).color,
                    }}>{(STATUS_META[selected.status] || {}).label}</Badge>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={openEdit}
                    data-testid="sp-edit-btn">
                    <PencilSimple size={14} className="mr-1" /> Éditer
                  </Button>
                  <Button size="sm" variant="outline" onClick={openReview}
                    data-testid="sp-review-btn">
                    <Plus size={14} className="mr-1" /> Ajouter point d'étape
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => downloadPdf(selected.id)}
                    data-testid="sp-pdf-btn">
                    <FilePdf size={14} className="mr-1" /> Exporter PDF
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => resendNotification(selected.id)}
                    data-testid="sp-notify-btn">
                    <EnvelopeSimple size={14} className="mr-1" />
                    {selected.parents_notified_at ? 'Re-notifier' : 'Notifier parents'}
                  </Button>
                </div>

                {selected.parents_notified_at && (
                  <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                    <BellRinging size={14} weight="fill" />
                    Notifié le {selected.parents_notified_at.slice(0, 10)} —
                    {(() => {
                      const rp = selected.parents_notification_report || {};
                      if (rp.no_parents) return ' aucun parent lié';
                      return ` ${rp.emailed || 0} email · ${rp.sms || 0} SMS · ${rp.push || 0} push`;
                    })()}
                  </div>
                )}

                {/* Subjects snapshot */}
                <div>
                  <h4 className="text-sm font-outfit font-semibold mb-1">Bilan matières</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(selected.subjects_snapshot || []).map((s) => {
                      const m = RISK_META[s.risk_level] || RISK_META.warning;
                      return (
                        <div key={s.subject}
                          className="p-2 rounded border text-xs"
                          style={{ borderColor: m.color + '55', background: m.bg + '55' }}>
                          <div className="flex justify-between items-center">
                            <b>{s.subject}</b>
                            <span style={{ color: m.color }}>{m.icon} {m.label}</span>
                          </div>
                          <div className="text-[10px] text-[#64748B]">
                            Moy. {s.mean}/20 · Min {s.min} · Tendance {s.slope ?? '—'}
                            {s.predicted_next != null && (
                              <> · Prévision {s.predicted_next}/20</>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {(selected.subjects_snapshot || []).length === 0 && (
                      <p className="text-xs text-[#94A3B8]">Aucune matière à risque.</p>
                    )}
                  </div>
                </div>

                {/* Recommendations */}
                <div>
                  <h4 className="text-sm font-outfit font-semibold mb-1">Recommandations</h4>
                  <ul className="space-y-1">
                    {(selected.recommendations || []).map((r, i) => {
                      const p = PRIORITY_META[r.priority] || PRIORITY_META.medium;
                      return (
                        <li key={i} className="text-xs p-2 rounded border border-[#E2E8F0] bg-white"
                          data-testid={`sp-reco-${i}`}>
                          <span className="font-bold" style={{ color: p.color }}>
                            [{p.label}]
                          </span>{' '}
                          <b>{r.subject}</b> — {r.text}
                          {r.custom && (
                            <span className="ml-1 text-[10px] text-blue-600">(personnalisé)</span>
                          )}
                        </li>
                      );
                    })}
                    {(selected.recommendations || []).length === 0 && (
                      <p className="text-xs text-[#94A3B8]">Aucune recommandation.</p>
                    )}
                  </ul>
                </div>

                {/* Actions checklist */}
                <div>
                  <h4 className="text-sm font-outfit font-semibold mb-1">Actions à mener</h4>
                  <ul className="space-y-1">
                    {(selected.actions || []).map((a, i) => (
                      <li key={i}
                        className="flex items-start gap-2 text-xs p-2 rounded border border-[#E2E8F0] bg-white"
                        data-testid={`sp-action-${i}`}>
                        <button type="button" onClick={() => toggleAction(i)}
                          className="mt-0.5"
                          data-testid={`sp-toggle-action-${i}`}>
                          {a.done
                            ? <CheckSquare size={16} weight="fill" className="text-emerald-600" />
                            : <Square size={16} className="text-[#94A3B8]" />}
                        </button>
                        <div className="flex-1">
                          <div className={a.done ? 'line-through text-[#94A3B8]' : ''}>
                            {a.label}
                          </div>
                          <div className="text-[10px] text-[#64748B]">
                            Échéance : {a.target_date}
                            {a.done_at && ` · Fait le ${a.done_at.slice(0, 10)}`}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Reviews */}
                {(selected.reviews || []).length > 0 && (
                  <div>
                    <h4 className="text-sm font-outfit font-semibold mb-1 flex items-center gap-1">
                      <ChatCircle size={14} /> Points d'étape
                    </h4>
                    <ul className="space-y-1">
                      {selected.reviews.map((r, i) => (
                        <li key={i}
                          className={`text-xs p-2 rounded border-l-2 ${
                            r.auto ? 'border-amber-400 bg-amber-50/50'
                                     : 'border-blue-400 bg-blue-50/50'}`}>
                          <div className="font-semibold flex items-center gap-2">
                            {r.date?.slice(0, 10)}
                            {r.auto && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">
                                AUTO J+30
                              </span>
                            )}
                            {r.improved === true && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                                ✔ AMÉLIORATION
                              </span>
                            )}
                            {r.improved === false && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-bold">
                                ⚠ PAS D'AMÉLIORATION
                              </span>
                            )}
                            {r.new_mean != null && (
                              <span className="ml-2 text-blue-700 font-normal">
                                → {r.new_mean}/20
                              </span>
                            )}
                          </div>
                          <div className="text-[#334155]">{r.note}</div>
                          <div className="text-[10px] text-[#94A3B8]">par {r.author}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Notes */}
                {selected.notes && (
                  <div>
                    <h4 className="text-sm font-outfit font-semibold mb-1">Notes</h4>
                    <p className="text-xs text-[#334155] whitespace-pre-wrap">{selected.notes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent data-testid="sp-review-dialog">
          <DialogHeader>
            <DialogTitle>Ajouter un point d'étape</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Note / observation *</Label>
              <Textarea rows={4} value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Progrès observés, difficultés persistantes, prochaines étapes…"
                data-testid="sp-review-note" />
            </div>
            <div>
              <Label>Nouvelle moyenne (optionnel, /20)</Label>
              <Input type="number" min="0" max="20" step="0.1" value={reviewMean}
                onChange={(e) => setReviewMean(e.target.value)}
                data-testid="sp-review-mean" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Annuler</Button>
            <Button onClick={submitReview} data-testid="sp-review-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="sp-edit-dialog">
          <DialogHeader>
            <DialogTitle>Éditer le plan</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Statut</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="sp-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="archived">Archivé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes complémentaires</Label>
              <Textarea rows={4} value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Contexte familial, aménagements pédagogiques, remarques du conseil de classe…"
                data-testid="sp-edit-notes" />
            </div>
            <div>
              <Label>Recommandations</Label>
              <div className="space-y-2">
                {editRecos.map((r, i) => (
                  <div key={i} className="p-2 border rounded flex gap-2 items-start">
                    <Input className="w-32 h-8" value={r.subject || ''}
                      onChange={(e) => {
                        const c = [...editRecos];
                        c[i] = { ...c[i], subject: e.target.value };
                        setEditRecos(c);
                      }} placeholder="Matière" />
                    <Textarea className="flex-1" rows={2} value={r.text || ''}
                      onChange={(e) => {
                        const c = [...editRecos];
                        c[i] = { ...c[i], text: e.target.value };
                        setEditRecos(c);
                      }} />
                    <Select value={r.priority || 'medium'}
                      onValueChange={(v) => {
                        const c = [...editRecos];
                        c[i] = { ...c[i], priority: v };
                        setEditRecos(c);
                      }}>
                      <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">Haute</SelectItem>
                        <SelectItem value="medium">Moyenne</SelectItem>
                        <SelectItem value="low">Basse</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost"
                      onClick={() => setEditRecos(editRecos.filter((_, k) => k !== i))}>
                      <Trash size={12} className="text-red-600" />
                    </Button>
                  </div>
                ))}
                <Button size="sm" variant="outline"
                  onClick={() => setEditRecos([
                    ...editRecos,
                    { subject: '', text: '', priority: 'medium', custom: true },
                  ])}
                  data-testid="sp-edit-add-reco">
                  <Plus size={12} className="mr-1" /> Ajouter une recommandation
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={submitEdit} data-testid="sp-edit-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
